import React, { useMemo } from 'react';

import {
  EmulatorType,
  Plan,
  PlanFamily,
  ProductFeature,
  Subscription,
  SubscriptionStatus,
  UsageSummary,
} from '@localstack/types';
import { EMULATOR_TYPES, TEST_IDS, SUB_ACTIVE_PAID_STATUSES, SUB_ACTIVE_STATUSES } from '@localstack/constants';
import { SubscriptionService, useApiGetter, UserService } from '@localstack/services';
import { Stack, Typography } from '@mui/material';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { useAggregatedSubscriptionInfo } from '~/hooks/useSubscriptions';

import { toSortedSafe } from '~/util/array';

import { EMULATORS } from '~/views/customer/PricingPlans/components/planFamilies';

import { Divider, GridComponent, SectionContainer } from '../utils';

import { EmulatorPrompt } from './EmulatorPrompt';
import { metricColumns } from './metricColumns';
import { SubscriptionRow } from './SubscriptionRow';
import {
  CancellingPlansPrompt,
  NoSubscriptionsPrompt,
  LegacyPlansPrompt,
  UpgradeToBase,
  UpgradeTrialPrompt,
  UpgradeToUltimate,
} from './utils';

// TODO: the fetching of subscription reloads as you switch between pages, we should check if we can cache it somehow

const findUpgradePlan = (plans: Plan[], family: PlanFamily): Optional<Plan> => {
  const matches = plans.filter((plan) => plan.public && plan.visible && plan.family === family);
  return matches.find((plan) => plan.interval_months === 12) ?? matches[0];
};

/**
 * Enterprise is the only family that can bundle Snowflake into a non-Snowflake plan.
 */
const emulatorsForSubscription = (subscription: Subscription, bundlesSnowflake: boolean): EmulatorType[] =>
  bundlesSnowflake && subscription.plan.family === PlanFamily.ENTERPRISE_PLANS
    ? [subscription.plan.emulator, EmulatorType.SNOWFLAKE]
    : [subscription.plan.emulator];

const EmulatorCTAs = ({ emulator, subs }: { emulator: EmulatorType; subs: Subscription[] }): JSX.Element => {
  const trialSub = subs.find((sub) => sub.plan?.family === PlanFamily.TRIAL_PLANS);
  const hasOnlyOneSub = subs.length === 1;
  const hasFreemium = subs.some((sub) => sub.plan?.family === PlanFamily.FREEMIUM_PLANS);
  const hasHobby = subs.some((sub) => sub.plan?.family === PlanFamily.HOBBY_PLANS);
  const hasLegacy = subs.some((sub) => sub.plan?.family === PlanFamily.LEGACY_PLANS);

  // Only the freemium and hobby upgrade boxes need the plan catalog, and only when this
  // emulator has a single subscription, so skip the request in every other case.
  const needsUpgradePlans = hasOnlyOneSub && (hasFreemium || hasHobby);
  const { data: plans } = useApiGetter(SubscriptionService, 'listPlans', [emulator], {
    defaultValue: [],
    enable: needsUpgradePlans,
  });

  const onlyTrial =
    subs.length === 1 &&
    !!trialSub &&
    [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(trialSub.status);

  const noPaidActiveCurrentSubscriptions = !subs.some((sub) => {
    const isPaid = SUB_ACTIVE_PAID_STATUSES.includes(sub.status);
    const isActive = !(sub?.cancel_at || sub?.cancel_at_period_end);
    const isNotLegacy = sub.plan.family !== PlanFamily.LEGACY_PLANS;
    return isPaid && isActive && isNotLegacy;
  });

  const showCancellingPrompt = !onlyTrial && subs.length > 0 && noPaidActiveCurrentSubscriptions && !hasLegacy;
  const showLegacyPrompt = hasLegacy && noPaidActiveCurrentSubscriptions;

  const basePlan = findUpgradePlan(plans ?? [], PlanFamily.BASE_PLANS);
  const ultimatePlan = findUpgradePlan(plans ?? [], PlanFamily.ULTIMATE_PLANS);

  return (
    <>
      {hasOnlyOneSub && hasFreemium && basePlan && <UpgradeToBase plan={basePlan} />}
      {hasOnlyOneSub && hasHobby && ultimatePlan && <UpgradeToUltimate plan={ultimatePlan} />}
      {onlyTrial && trialSub && (
        <UpgradeTrialPrompt emulator={emulator} trialEndDate={trialSub.cancel_at ?? trialSub.current_period_end} />
      )}
      {showCancellingPrompt && <CancellingPlansPrompt emulator={emulator} />}
      {showLegacyPrompt && <LegacyPlansPrompt emulator={emulator} />}
    </>
  );
};

export const Subscriptions = (): JSX.Element => {
  const { hasFeature } = useAuthProvider();
  const hasSnowflakeFeature = hasFeature(ProductFeature.FEATURE_SNOWFLAKE);
  const { data: subscriptionsData, isLoading } = useAggregatedSubscriptionInfo();
  const { subscriptions, usageData = {}, ...subscriptionsDetails } = subscriptionsData || {};

  const { data: cards, isLoading: isFetchingCards } = useApiGetter(UserService, 'listCreditCards', [], {
    defaultValue: [],
  });

  const subscriptionsToShow = useMemo(() => {
    // we also show subscriptions where the payment may have failed so users can retry via invoice
    // we also show unpaid subscriptions (> 30 days and multiple Stripe payment retries) so that the users can contact Finance
    const active = (subscriptions ?? []).filter(({ status }) =>
      [SubscriptionStatus.INCOMPLETE, SubscriptionStatus.UNPAID, ...SUB_ACTIVE_STATUSES].includes(status),
    );
    return toSortedSafe(active, (a, b) => b.created - a.created);
  }, [subscriptions]);

  // Group subscriptions per emulator, in stable emulator order. Keyed off every known emulator
  // rather than the offered ones, so a plan granted before its product is offered still shows.
  const subscriptionsGroupedByEmulator = useMemo(
    () =>
      EMULATOR_TYPES.map((emulator) => ({
        emulator,
        subs: subscriptionsToShow.filter((sub) => sub.plan.emulator === emulator),
      })).filter((group) => group.subs.length > 0),
    [subscriptionsToShow],
  );

  // An AWS Enterprise plan can bundle Snowflake access, so Snowflake needs neither its own box nor
  // the "try now" callout. A standalone Snowflake subscription grants the entitlement on its own,
  // in which case the AWS plan covers nothing.
  // TODO: remove once all AWS plans that bundle Snowflake as feature are moved to their own Enterprise plans.
  const hasSnowflakeSubscription = subscriptionsToShow.some((sub) => sub.plan.emulator === EmulatorType.SNOWFLAKE);

  const snowflakeCoveredByAwsEnterprise =
    !hasSnowflakeSubscription &&
    hasSnowflakeFeature &&
    subscriptionsToShow.some(
      (sub) => sub.plan.emulator !== EmulatorType.SNOWFLAKE && sub.plan.family === PlanFamily.ENTERPRISE_PLANS,
    );

  // Emulators we offer that the workspace does not have yet, so only these get a "try now" prompt.
  const emulatorsWithoutSubscription = useMemo(() => {
    const withSub = new Set(subscriptionsGroupedByEmulator.map((entry) => entry.emulator));
    return EMULATORS.filter((emulator) => !withSub.has(emulator));
  }, [subscriptionsGroupedByEmulator]);

  if (isLoading || isFetchingCards) {
    return (
      <SectionContainer>
        <GridComponent columns="1fr">
          <Typography color="text.tertiary">Fetching your subscriptions...</Typography>
        </GridComponent>
      </SectionContainer>
    );
  }

  if (subscriptionsToShow.length === 0) {
    return (
      <SectionContainer>
        <NoSubscriptionsPrompt />
      </SectionContainer>
    );
  }

  return (
    <Stack gap={2}>
      {subscriptionsGroupedByEmulator.map((group) => {
        const { emulator } = group;
        const columns = metricColumns(group.subs, !!subscriptionsDetails.computeUsage);

        return (
          <SectionContainer key={emulator} data-testid={TEST_IDS.BILLING_EMULATOR_GROUP(emulator)}>
            <GridComponent columns={`1fr ${columns.map(() => 'max-content').join(' ')} min-content`}>
              {group.subs.map((subscription, index) => (
                <React.Fragment key={subscription.id}>
                  <SubscriptionRow
                    subscription={subscription}
                    subscriptionsDetails={{
                      ...subscriptionsDetails,
                      usageData: usageData[subscription.id] as UsageSummary,
                    }}
                    cards={cards}
                    emulators={emulatorsForSubscription(subscription, snowflakeCoveredByAwsEnterprise)}
                    metricColumns={columns}
                  />
                  {index < group.subs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </GridComponent>
            <EmulatorCTAs emulator={group.emulator} subs={group.subs} />
          </SectionContainer>
        );
      })}

      {emulatorsWithoutSubscription.map((emulator) => {
        const skipSnowflakePromptWhenCovered = emulator === EmulatorType.SNOWFLAKE && snowflakeCoveredByAwsEnterprise;
        return skipSnowflakePromptWhenCovered ? null : <EmulatorPrompt key={emulator} emulator={emulator} />;
      })}
    </Stack>
  );
};
